import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import type { SingleValue } from 'react-select'
import type { StaticSelectProps, SelectOption, SelectOptionGroup } from './Select.types'
import {
  Label,
  buildClassNames,
  selectLayoutStyles,
  DropdownIndicator,
  ClearIndicator,
  IndicatorsContainer,
  RdControl,
  MenuListWithSearch,
} from './Select.utils'

export default function SelectStatic({
  name,
  options: propOptions,
  initialValue,
  label,
  placeholder,
  required,
  disabled,
  floating,
  size = 'medium',
  error,
  helpText,
  clearable,
  searchable,
  onChange,
  setterRef,
  menuPlacement = 'auto',
  autoWidth,
}: StaticSelectProps) {
  const isGrouped = propOptions.length > 0 && 'options' in propOptions[0]

  const flatOptions = useMemo(() => {
    if (isGrouped) {
      return (propOptions as SelectOptionGroup[]).flatMap(g => g.options)
    }
    return propOptions as SelectOption[]
  }, [propOptions, isGrouped])

  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [extraOptions, setExtraOptions] = useState<SelectOption[]>([])
  const [value, setValue] = useState<SelectOption | null>(
    () => flatOptions.find(o => o.value === initialValue) ?? null,
  )

  const propOptionsRef = useRef(flatOptions)
  propOptionsRef.current = flatOptions

  const options = useMemo(() => {
    if (isGrouped) return propOptions
    return extraOptions.length
      ? [...propOptions as SelectOption[], ...extraOptions.filter(e => !(propOptions as SelectOption[]).some(p => p.value === e.value))]
      : propOptions
  }, [propOptions, extraOptions, isGrouped])

  const prevInitialValueRef = useRef(initialValue)
  useEffect(() => {
    const initialValueChanged = prevInitialValueRef.current !== initialValue
    prevInitialValueRef.current = initialValue

    if (initialValueChanged) {
      setValue(
        initialValue == null
          ? null
          : flatOptions.find(o => o.value === initialValue) ?? null,
      )
    } else if (initialValue != null) {
      setValue(prev => prev ?? flatOptions.find(o => o.value === initialValue) ?? null)
    }
  }, [flatOptions, initialValue])

  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!setterRef) return
    setterRef.current = (val: string, lbl?: string) => {
      if (!val) {
        setValue(null)
        return
      }
      const existing = propOptionsRef.current.find(o => o.value === val)
      if (existing) {
        setValue(existing)
        return
      }
      const opt: SelectOption = { value: val, label: lbl ?? val }
      if (!isGrouped) {
        setExtraOptions(prev => (prev.some(o => o.value === val) ? prev : [...prev, opt]))
      }
      setValue(opt)
    }
    return () => {
      setterRef.current = null
    }
  }, [setterRef, isGrouped])

  const handleChange = (opt: SingleValue<SelectOption>) => {
    setValue(opt ?? null)
    onChange?.(opt?.value ?? '')
    if (searchable) {
      closeMenu()
    }
  }

  const closeMenu = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
  }, [])

  // Закрывать меню при клике вне компонента (когда фокус на поиске)
  useEffect(() => {
    if (!searchable || !isOpen) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [searchable, isOpen, closeMenu])

  const handleSearchMouseDown = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation()
  }, [])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') closeMenu()
  }, [closeMenu])

  const menuOptions = useMemo(() => {
    if (!searchable || !searchQuery) return options
    const q = searchQuery.toLowerCase()
    if (isGrouped) {
      return (options as SelectOptionGroup[])
        .map(g => ({ ...g, options: g.options.filter(o => o.label.toLowerCase().includes(q)) }))
        .filter(g => g.options.length > 0)
    }
    return (options as SelectOption[]).filter(o => o.label.toLowerCase().includes(q))
  }, [searchable, searchQuery, options, isGrouped])

  const selectComponents = useMemo(() => {
    const base = { DropdownIndicator, ClearIndicator, IndicatorsContainer, Control: RdControl }
    if (!searchable) return base
    return { ...base, MenuList: MenuListWithSearch as any }
  }, [searchable])

  return (
    <>
      {label && !floating && <Label text={label} required={required} />}
      <div ref={containerRef} style={{ display: 'contents' }}>
        <Select<SelectOption>
          unstyled
          name={name}
          options={menuOptions}
          value={value}
          placeholder={floating ? '' : (placeholder ?? '')}
          isDisabled={disabled}
          isClearable={clearable}
          isSearchable={false}
          filterOption={searchable ? null : undefined}
          menuIsOpen={searchable ? isOpen : undefined}
          menuPlacement={menuPlacement}
          minMenuHeight={300}
          menuShouldScrollIntoView={false}
          onMenuOpen={() => requestAnimationFrame(() => setIsOpen(true))}
          onMenuClose={() => { if (!searchable) setIsOpen(false) }}
          onFocus={() => setIsFocused(true)}
          onBlur={(e: React.FocusEvent) => {
            setIsFocused(false)
            if (!searchable) return
            // Фокус ушёл внутрь меню (поиск/опция) — это не выход.
            const next = e.relatedTarget as Node | null
            if (next && containerRef.current?.contains(next)) return
            closeMenu()
          }}
          onChange={handleChange}
          classNames={buildClassNames({ size, floating, error, isOpen, isFocused })}
          styles={selectLayoutStyles}
          components={selectComponents}
          formatGroupLabel={(group) => (
            <div className="rd-dropdown__group-header">{group.label}</div>
          )}
          floatingLabel={floating ? label : undefined}
          floatingRequired={floating ? required : undefined}
          sizerText={autoWidth ? (label || placeholder) : undefined}
          menuSearchQuery={searchable ? searchQuery : undefined}
          onMenuSearchChange={searchable ? setSearchQuery : undefined}
          onMenuSearchMouseDown={searchable ? handleSearchMouseDown : undefined}
          onMenuSearchKeyDown={searchable ? handleSearchKeyDown : undefined}
          noOptionsMessage={() => 'Нет вариантов'}
        />
      </div>
      {error && <span className="rd-dropdown__error-text">{error}</span>}
      {helpText && <span className="rd-dropdown__helper-text">{helpText}</span>}
    </>
  )
}
